import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { showNewMessage } from '@intercom/messenger-js-sdk';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Badge } from '@plato/ui-legacy/components/ui/badge';
import { IconButton } from '@plato/ui/candidates/icon-button';
import { ToolbarIconButton } from '@plato/ui/candidates/toolbar-icon-button';
import { InputGroupAddon } from '@plato/ui/primitives/input-group';
import { ToolbarButton } from '@plato/ui/primitives/toolbar-button';
import { Typography } from '@plato/ui/primitives/typography';
import {
    ChevronLeftIcon,
    HeadsetIcon,
    HistoryIcon,
    InfoIcon,
    MicIcon,
    PlusIcon,
    RotateCcwIcon,
    ThumbsDownIcon,
    ThumbsUpIcon,
    XIcon,
} from 'lucide-react';
import { useEffect, useState, type ReactElement, type RefObject } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { AiChatSupportTrigger } from '../../analytics/types/analytics-events.ts';
import { RecordingDialog } from '../../voice-recording/components/recording-dialog.tsx';
import {
    lastTextPart,
    messagePlainText,
    type AskPlatoChat,
    type AskPlatoChatMessage,
} from '../hooks/use-ask-plato-chat.ts';
import { useToolCaption } from '../hooks/use-tool-caption.ts';
import { AskPlatoHistory } from './ask-plato-history.tsx';
import { ChatErrorState } from './chat-error-state.tsx';
import { MessageActionButton, MessageActions } from './message-actions.tsx';
import { bubbleCopyText, MessageBubble, MessageCopyButton } from './message-copy.tsx';
import {
    MessageScroller,
    MessageScrollerButton,
    MessageScrollerContent,
    MessageScrollerItem,
    MessageScrollerProvider,
    MessageScrollerViewport,
    useMessageScroller,
} from './message-scroller.tsx';
import { Message, MessageContent } from './message.tsx';
import { PromptInput, PromptInputSubmit, PromptInputTextarea } from './prompt-input.tsx';
import { ThinkingDots } from './thinking-dots.tsx';
import { ToolCallRow } from './tool-call-row.tsx';

export type AskPlatoPanelProps = {
    chat: AskPlatoChat;
    /** Wired by the provider so the surface can focus the composer on open. */
    composerRef?: RefObject<HTMLTextAreaElement | null>;
    /** Called after a page link in a reply navigated in this tab. */
    onPageLinkNavigated?: () => void;
    /** Seeds the composer on mount, with the cursor at `caret`. */
    initialDraft?: { text: string; caret: number };
};

/**
 * Index of the message's last text part.
 *
 * Every tool row after it belongs to the same round. The server starts a
 * round's tool calls together and streams no text until all of them have
 * returned, so while the reply is still streaming, those rows are still
 * running. Text arriving after a tool row means its round is done.
 *
 * The action row under the last bubble also takes over that bubble's copy button.
 */
function lastTextPartIndex(message: AskPlatoChatMessage): number {
    return message.parts.findLastIndex((part) => part.type === 'text');
}

/** Which outcome put the support button on screen, most specific cause first. */
function supportTriggerFor(status: AskPlatoChatMessage['status'], hasError: boolean): AiChatSupportTrigger {
    // `hasError` wins: a thrown stream has no status the server stands behind.
    if (hasError) {
        return 'error';
    }
    if (status === 'max_rounds_reached') {
        return 'max_rounds_reached';
    }
    if (status === 'answered_without_tools') {
        return 'answered_without_tools';
    }
    return 'failure';
}

export function AskPlatoPanel(props: AskPlatoPanelProps): ReactElement {
    // The scroller provider sits above the composer too, so sending a message
    // can re-pin the thread to its end after the user scrolled up.
    return (
        <MessageScrollerProvider>
            <AskPlatoPanelContent {...props} />
        </MessageScrollerProvider>
    );
}

function AskPlatoPanelContent({
    chat,
    composerRef,
    onPageLinkNavigated,
    initialDraft,
}: AskPlatoPanelProps): ReactElement {
    const { t } = useTranslation('ask-plato');
    const getToolCaption = useToolCaption();
    const sendEvent = useAnalytics();
    const { scrollToEnd } = useMessageScroller();
    const [draft, setDraft] = useState(initialDraft?.text ?? '');

    // A seeded draft is a question to finish: focus it with the cursor in the
    // gap, after the surface has run its own initial focus.
    useEffect(() => {
        if (!initialDraft) {
            return;
        }
        const frame = requestAnimationFrame(() => {
            const composer = composerRef?.current;
            composer?.focus();
            composer?.setSelectionRange(initialDraft.caret, initialDraft.caret);
        });
        return () => {
            cancelAnimationFrame(frame);
        };
    }, [initialDraft, composerRef]);
    const [view, setView] = useState<'chat' | 'history'>('chat');

    const {
        messages,
        isStreaming,
        isLoading,
        hasError,
        title,
        contextLimitReached,
        conversations,
        sendMessage,
        toggleRating,
        retry,
        startNewChat,
        reportCopied,
        reportPageLinkClicked,
        resumeConversation,
    } = chat;
    const lastMessage = messages.at(-1);
    const lastMessageId = lastMessage?.id;
    // Sending, switching or resuming a conversation has to wait for both.
    const isBusy = isStreaming || isLoading;

    // The tool-round backstop cut the reply off mid-task. The user gets the
    // same error treatment as `failure`, but the escalation names its own
    // cause so the two can be told apart in analytics.
    const replyCutOff = lastMessage?.status === 'max_rounds_reached';
    // A reply fails in three ways: the server classifies it `failure` and
    // closes the stream cleanly, the backstop cuts it off, or the stream
    // throws and never reports at all. All are the same thing to the user.
    const replyFailed = hasError || lastMessage?.status === 'failure' || replyCutOff;
    // `answered_without_tools` is not a failure — the model answered without reaching
    // for data, typically asking something back. There is real text on screen,
    // so it gets the escalation without the error line above it.
    const replyWithoutTools = lastMessage?.status === 'answered_without_tools';
    const supportTrigger = supportTriggerFor(lastMessage?.status, hasError);
    const lastQuestion = messages.findLast((message) => message.role === 'user');

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            {/* Level 2 so the toolbar buttons drop their off-container outline. */}
            <div
                data-container="2"
                className="flex shrink-0 items-center gap-cozy border-b border-line-weakest px-relaxed py-cozy"
            >
                {view === 'history' ? (
                    <>
                        <ToolbarIconButton
                            label={t('back')}
                            className="-ml-cozy"
                            onClick={() => {
                                sendEvent('ai_chat_history_closed', { resumed: false });
                                setView('chat');
                            }}
                        >
                            <ChevronLeftIcon className="size-5" />
                        </ToolbarIconButton>
                        <Typography variant="body-emphasis" className="flex-1">
                            {t('history')}
                        </Typography>
                    </>
                ) : (
                    <>
                        <div className="flex min-w-0 flex-1 flex-col">
                            <div className="flex items-center gap-cozy">
                                <Typography variant="body-emphasis">{t('title')}</Typography>
                                {/* Matches the `Alpha` marker on Plato Revenue — same hardcoded,
                                    untranslated wording for a product-maturity label. */}
                                <Badge variant="blue" className="text-xs">
                                    Alpha
                                </Badge>
                            </div>
                            <Typography variant="caption" className="truncate">
                                {title ?? t('new_chat')}
                            </Typography>
                        </div>
                        <ToolbarIconButton
                            label={t('new_chat')}
                            disabled={isBusy || messages.length === 0}
                            onClick={() => {
                                startNewChat('user');
                            }}
                        >
                            <PlusIcon className="size-5" />
                        </ToolbarIconButton>
                        <ToolbarIconButton
                            label={t('history')}
                            disabled={isBusy}
                            onClick={() => {
                                sendEvent('ai_chat_history_opened', {
                                    conversation_count: conversations?.length,
                                });
                                setView('history');
                            }}
                        >
                            <HistoryIcon className="size-4.5" />
                        </ToolbarIconButton>
                    </>
                )}
                <DialogPrimitive.Close
                    data-slot="ask-plato-close"
                    render={
                        <IconButton label={t('close')} variant="secondary">
                            <XIcon className="size-5" />
                        </IconButton>
                    }
                />
            </div>

            {view === 'history' ? (
                <AskPlatoHistory
                    conversations={conversations}
                    disabled={isBusy}
                    onResume={(conversation) => {
                        sendEvent('ai_chat_history_closed', { resumed: true });
                        resumeConversation(conversation);
                        setView('chat');
                    }}
                />
            ) : (
                <>
                    <MessageScroller className="min-h-0 flex-1">
                        <MessageScrollerViewport>
                            <MessageScrollerContent className="gap-default px-relaxed py-default">
                                {/* Negative margins undo the scroller's padding so the
                                        disclaimer strip runs edge to edge. */}
                                <div className="-mx-relaxed -mt-default flex items-start gap-cozy border-b border-line-weakest bg-container-inset px-relaxed py-cozy">
                                    <InfoIcon className="mt-0.5 size-3.5 shrink-0 text-content-weaker" />
                                    <Typography variant="caption">{t('disclaimer')}</Typography>
                                </div>

                                <MessageBubble
                                    variant="bubble"
                                    className="rounded-tl-tight"
                                    text={t('welcome')}
                                />

                                {messages.map((message) => {
                                    const streamingThis = isStreaming && message.id === lastMessageId;
                                    const lastTextIndex = lastTextPartIndex(message);
                                    // Rating thumbs (and copy) — only under the LAST
                                    // assistant message of a finished turn, never
                                    // mid-answer.
                                    const showsActions =
                                        message.role === 'assistant' &&
                                        message.id === lastMessageId &&
                                        !isStreaming &&
                                        message.status !== 'failure' &&
                                        messagePlainText(message).length > 0;
                                    return (
                                        <MessageScrollerItem
                                            key={message.id}
                                            messageId={message.id}
                                            className="flex flex-col gap-default"
                                        >
                                            {message.parts.map((part, index) =>
                                                part.type === 'text' ? (
                                                    <MessageBubble
                                                        // Parts are append-only per message,
                                                        // so the position is stable.
                                                        key={`${message.id}-${index}`}
                                                        align={message.role === 'user' ? 'end' : 'start'}
                                                        variant={message.role === 'user' ? 'solid' : 'bubble'}
                                                        className={
                                                            message.role === 'user'
                                                                ? 'rounded-tr-tight'
                                                                : 'rounded-tl-tight'
                                                        }
                                                        text={part.text}
                                                        markdown={message.role === 'assistant'}
                                                        streaming={streamingThis && index === lastTextIndex}
                                                        // The action row beneath the last bubble
                                                        // carries its copy instead.
                                                        gutterCopy={
                                                            !(showsActions && index === lastTextIndex)
                                                        }
                                                        onCopied={(via) => {
                                                            reportCopied(message, via);
                                                        }}
                                                        onPageLinkClick={(click) => {
                                                            reportPageLinkClicked(message, click);
                                                            if (!click.newTab) {
                                                                onPageLinkNavigated?.();
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <ToolCallRow
                                                        key={part.id}
                                                        text={getToolCaption(part)}
                                                        // The status chunk settles the call, so
                                                        // sibling calls stop animating one by one.
                                                        inProgress={part.status === 'running'}
                                                    />
                                                ),
                                            )}

                                            {/* A reply that hasn't produced anything yet. */}
                                            {message.parts.length === 0 && streamingThis && (
                                                <Message align="start">
                                                    <MessageContent
                                                        variant="bubble"
                                                        className="rounded-tl-tight"
                                                    >
                                                        <ThinkingDots label={t('thinking')} />
                                                    </MessageContent>
                                                </Message>
                                            )}

                                            {showsActions && (
                                                <MessageActions className="-mt-tight px-tight">
                                                    <MessageActionButton
                                                        label={t('like')}
                                                        active={message.rating === 'like'}
                                                        verdict="positive"
                                                        onClick={() => {
                                                            toggleRating(message.id, 'like');
                                                        }}
                                                    >
                                                        <ThumbsUpIcon />
                                                    </MessageActionButton>
                                                    <MessageActionButton
                                                        label={t('dislike')}
                                                        active={message.rating === 'dislike'}
                                                        verdict="negative"
                                                        onClick={() => {
                                                            toggleRating(message.id, 'dislike');
                                                        }}
                                                    >
                                                        <ThumbsDownIcon />
                                                    </MessageActionButton>
                                                    {/* The freshest answer is the one you
                                                                copy, so it is always visible here. It
                                                                stands in for the last bubble's gutter
                                                                button, so it copies that bubble only. */}
                                                    <MessageCopyButton
                                                        text={() =>
                                                            bubbleCopyText(
                                                                lastTextPart(message)?.text ?? '',
                                                                true,
                                                            )
                                                        }
                                                        onCopied={() => {
                                                            reportCopied(message, 'action_row');
                                                        }}
                                                    />
                                                </MessageActions>
                                            )}
                                        </MessageScrollerItem>
                                    );
                                })}

                                {/* A closed conversation takes the failure state's place.
                                    The way out is a new chat, not a retry. */}
                                {contextLimitReached ? (
                                    <ChatErrorState
                                        title={t('context_limit_reached')}
                                        explanation={t('context_limit_reached_explanation')}
                                    >
                                        <ToolbarButton
                                            variant="primary"
                                            disabled={isBusy}
                                            onClick={() => {
                                                startNewChat('user');
                                            }}
                                        >
                                            <PlusIcon className="size-4" />
                                            {t('start_new_chat')}
                                        </ToolbarButton>
                                    </ChatErrorState>
                                ) : (
                                    (replyFailed || replyWithoutTools) && (
                                        <ChatErrorState
                                            title={replyFailed ? t('error') : undefined}
                                            explanation={replyFailed ? t('error_explanation') : undefined}
                                        >
                                            {replyFailed && (
                                                <ToolbarButton
                                                    variant="primary"
                                                    onClick={() => {
                                                        retry();
                                                        scrollToEnd();
                                                    }}
                                                >
                                                    <RotateCcwIcon className="size-4" />
                                                    {t('retry')}
                                                </ToolbarButton>
                                            )}
                                            <ToolbarButton
                                                variant="secondary"
                                                onClick={() => {
                                                    sendEvent('ai_chat_support_requested', {
                                                        trigger: supportTrigger,
                                                        message_index: lastMessage?.messageIndex ?? 0,
                                                    });
                                                    // Pre-fills the composer with the question
                                                    // that failed, so support opens on the
                                                    // case rather than an empty box.
                                                    showNewMessage(
                                                        t('support_message', {
                                                            question: lastQuestion
                                                                ? messagePlainText(lastQuestion)
                                                                : '',
                                                        }),
                                                    );
                                                }}
                                            >
                                                <HeadsetIcon className="size-4" />
                                                {t('ask_support')}
                                            </ToolbarButton>
                                        </ChatErrorState>
                                    )
                                )}
                            </MessageScrollerContent>
                        </MessageScrollerViewport>
                        <MessageScrollerButton label={t('scroll_to_bottom')} />
                    </MessageScroller>

                    <div className="shrink-0 border-t border-line-weakest px-default py-cozy pb-[max(var(--spacing-cozy),env(safe-area-inset-bottom))]">
                        <PromptInput
                            disabled={contextLimitReached}
                            onSubmit={(event) => {
                                event.preventDefault();
                                if (isBusy || contextLimitReached || draft.trim().length === 0) {
                                    return;
                                }
                                sendMessage(draft);
                                setDraft('');
                                scrollToEnd();
                                // A tap on send moves focus off the textarea and
                                // closes the keyboard. Enter does not, so on touch
                                // the keyboard would cover the reply. Blur to match.
                                if (isMobile) {
                                    const composer = event.currentTarget.elements.namedItem('prompt');
                                    if (composer instanceof HTMLTextAreaElement) {
                                        composer.blur();
                                    }
                                }
                            }}
                        >
                            <PromptInputTextarea
                                ref={composerRef}
                                // Touch bumps the composer to the 44px target.
                                className="[@media(hover:none)]:py-3"
                                rows={1}
                                name="prompt"
                                disabled={contextLimitReached}
                                value={draft}
                                onChange={(event) => {
                                    setDraft(event.target.value);
                                }}
                                placeholder={t('placeholder')}
                            />
                            <InputGroupAddon align="inline-end">
                                <ToolbarIconButton
                                    label={t('voice__start_recording', { ns: 'common' })}
                                    className="[@media(hover:none)]:h-11 [@media(hover:none)]:min-w-11"
                                    disabled={isBusy || contextLimitReached}
                                    onClick={() => {
                                        void DialogStateManager.show(RecordingDialog, {
                                            trackingContext: { source: 'ask_plato' },
                                            onTranscribed: (text) => {
                                                setDraft((current) =>
                                                    current.trim().length > 0 ? `${current} ${text}` : text,
                                                );
                                            },
                                        });
                                    }}
                                >
                                    <MicIcon />
                                </ToolbarIconButton>
                                <PromptInputSubmit
                                    className="[@media(hover:none)]:h-11 [@media(hover:none)]:min-w-11"
                                    disabled={isBusy || contextLimitReached || draft.trim().length === 0}
                                    label={t('send')}
                                />
                            </InputGroupAddon>
                        </PromptInput>
                    </div>
                </>
            )}
        </div>
    );
}
